import { forwardRef, useId, useState, type KeyboardEvent, type TextareaHTMLAttributes, useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../../../utils';
import { ArrowUp, Square, Paperclip, AtSign, Mic } from 'lucide-react';
import { Spinner } from '../../atoms';

export interface ChatInputProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onSubmit' | 'value' | 'onChange'> {
  onSubmit?: (value: string) => void;
  isLoading?: boolean;
  maxLength?: number;
  showCharacterCount?: boolean;
  submitButtonLabel?: string;
  /** Show toolbar with action buttons */
  showToolbar?: boolean;
  /** Left side toolbar actions */
  toolbarActions?: ReactNode;
  /** Callback for attachment button */
  onAttachment?: () => void;
  /** Optional visible label beside the attachment icon. */
  attachmentButtonLabel?: string;
  /** Callback for mention button */
  onMention?: () => void;
  /** Callback for voice input */
  onVoiceInput?: () => void;
  /** Callback for stop generation */
  onStop?: () => void;
  /** Controlled value — when provided, component becomes controlled */
  value?: string;
  /** Controlled onChange — called when text changes in controlled mode */
  onChange?: (value: string) => void;
  /** Slot rendered above the textarea for file preview chips etc. */
  attachmentSlot?: ReactNode;
  /**
   * Why sending is not possible right now. When set, the send button and Enter
   * do nothing and the reason is shown under the input; typing stays possible.
   */
  sendDisabledReason?: string;
  /**
   * Files are staged with the message. Sending then needs no text: the
   * message goes out with its files, and onSubmit receives ''.
   */
  hasAttachments?: boolean;
  /**
   * Why files cannot be attached here. When set, the attach button shows as
   * disabled with this reason, which is also shown under the input.
   */
  attachmentDisabledReason?: string;
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      className,
      onSubmit,
      isLoading = false,
      maxLength,
      showCharacterCount = false,
      submitButtonLabel = 'Send',
      showToolbar = true,
      toolbarActions,
      onAttachment,
      attachmentButtonLabel,
      onMention,
      onVoiceInput,
      onStop,
      disabled,
      placeholder = 'Type a message...',
      value: controlledValue,
      onChange: controlledOnChange,
      attachmentSlot,
      sendDisabledReason,
      hasAttachments = false,
      attachmentDisabledReason,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState('');
    const reasonId = useId();
    const attachReasonId = useId();
    const [rows, setRows] = useState(1);
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    // Controlled vs uncontrolled
    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;
    const setValue = isControlled
      ? (v: string) => controlledOnChange?.(v)
      : setInternalValue;

    const handleRef = (node: HTMLTextAreaElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    useEffect(() => {
      if (internalRef.current) {
        const lineHeight = 24;
        const previousRows = internalRef.current.rows;
        internalRef.current.rows = 1;
        const currentRows = Math.floor(internalRef.current.scrollHeight / lineHeight);
        if (currentRows === previousRows) {
          internalRef.current.rows = currentRows;
        }
        const newRows = Math.min(Math.max(currentRows, 1), 6);
        setRows(newRows);
        internalRef.current.rows = newRows;
      }
    }, [value]);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    const handleSubmit = () => {
      if (sendDisabledReason) return;
      if ((value.trim() || hasAttachments) && !isLoading && onSubmit) {
        onSubmit(value.trim());
        if (!isControlled) {
          setInternalValue('');
          setRows(1);
        }
      }
    };

    const handleStop = () => {
      onStop?.();
    };

    const isDisabled = disabled || isLoading;
    const currentLength = value.length;
    const isOverLimit = maxLength ? currentLength > maxLength : false;
    const canSubmit = (value.trim() || hasAttachments) && !isDisabled && !isOverLimit && !sendDisabledReason;
    const describedBy = [showCharacterCount ? 'char-count' : undefined, sendDisabledReason ? reasonId : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

    return (
      <div className={cn('w-full max-w-3xl mx-auto', className)}>
        <div
          className={cn(
            'relative bg-white rounded-2xl border border-slate-200 shadow-lg shadow-slate-200/50',
            'dark:bg-slate-900 dark:border-slate-700 dark:shadow-slate-900/50',
            'transition-all duration-150',
            'focus-within:border-slate-300 focus-within:shadow-xl focus-within:shadow-slate-200/60',
            'dark:focus-within:border-slate-600 dark:focus-within:shadow-slate-900/60'
          )}
        >
          {/* Attachment slot — rendered above textarea */}
          {attachmentSlot && (
            <div className="px-4 pt-3">
              {attachmentSlot}
            </div>
          )}

          {/* Textarea area */}
          <div className="px-4 pt-4 pb-2">
            <textarea
              ref={handleRef}
              rows={rows}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isDisabled}
              placeholder={placeholder}
              maxLength={maxLength}
              className={cn(
                'w-full min-h-[24px] max-h-[200px] overflow-y-auto',
                'bg-transparent border-0 resize-none',
                'text-slate-900 text-base leading-relaxed placeholder-slate-400',
                'focus:outline-none focus:ring-0',
                'disabled:cursor-not-allowed disabled:opacity-50',
                'dark:text-white dark:placeholder-slate-500',
                isOverLimit && 'text-rose-600 dark:text-rose-400'
              )}
              aria-label="Chat message input"
              aria-invalid={isOverLimit}
              aria-describedby={describedBy}
              {...props}
            />
          </div>

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-3 pb-3">
            {/* Left side - Action buttons */}
            <div className="flex items-center gap-1">
              {toolbarActions}
              {showToolbar && !toolbarActions && (
                <>
                  {(onAttachment || attachmentDisabledReason) && (
                    <button
                      type="button"
                      onClick={onAttachment}
                      disabled={isDisabled || !!attachmentDisabledReason}
                      title={attachmentDisabledReason}
                      aria-describedby={attachmentDisabledReason ? attachReasonId : undefined}
                      className={cn(
                        'inline-flex items-center gap-2 p-2 rounded-lg text-slate-500 transition-colors',
                        'hover:bg-slate-100 hover:text-slate-700',
                        'dark:hover:bg-slate-800 dark:hover:text-slate-300',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                      aria-label={attachmentButtonLabel ?? 'Attach file'}
                    >
                      <Paperclip className="h-5 w-5 shrink-0" />
                      {attachmentButtonLabel && <span className="text-sm">{attachmentButtonLabel}</span>}
                    </button>
                  )}
                  {onMention && (
                    <button
                      type="button"
                      onClick={onMention}
                      disabled={isDisabled}
                      className={cn(
                        'p-2 rounded-lg text-slate-500 transition-colors',
                        'hover:bg-slate-100 hover:text-slate-700',
                        'dark:hover:bg-slate-800 dark:hover:text-slate-300',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                      aria-label="Mention"
                    >
                      <AtSign className="h-5 w-5" />
                    </button>
                  )}
                  {onVoiceInput && (
                    <button
                      type="button"
                      onClick={onVoiceInput}
                      disabled={isDisabled}
                      className={cn(
                        'p-2 rounded-lg text-slate-500 transition-colors',
                        'hover:bg-slate-100 hover:text-slate-700',
                        'dark:hover:bg-slate-800 dark:hover:text-slate-300',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                      aria-label="Voice input"
                    >
                      <Mic className="h-5 w-5" />
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Right side - Character count and send/stop button */}
            <div className="flex items-center gap-3">
              {showCharacterCount && maxLength && (
                <span
                  id="char-count"
                  className={cn(
                    'text-xs',
                    isOverLimit
                      ? 'text-rose-600 dark:text-rose-400'
                      : 'text-slate-400 dark:text-slate-500'
                  )}
                  aria-live="polite"
                >
                  {currentLength}/{maxLength}
                </span>
              )}

              {isLoading && onStop ? (
                <button
                  type="button"
                  onClick={handleStop}
                  className={cn(
                    'flex items-center justify-center h-9 w-9 rounded-lg',
                    'bg-slate-900 text-white',
                    'hover:bg-slate-800 transition-colors',
                    'dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100'
                  )}
                  aria-label="Stop generation"
                >
                  <Square className="h-4 w-4" fill="currentColor" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  title={sendDisabledReason}
                  aria-describedby={sendDisabledReason ? reasonId : undefined}
                  className={cn(
                    'flex items-center justify-center h-9 w-9 rounded-lg transition-colors',
                    canSubmit
                      ? 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100'
                      : 'bg-slate-100 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-600'
                  )}
                  aria-label={submitButtonLabel}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="border-current border-t-transparent" />
                  ) : (
                    <ArrowUp className="h-5 w-5" />
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Helper text, or why sending is not possible right now */}
        {sendDisabledReason ? (
          <p id={reasonId} role="status" className="mt-2 text-center text-xs text-amber-700 dark:text-amber-300">
            {sendDisabledReason}
          </p>
        ) : (
          <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">
            <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-mono">Enter</kbd>
            {' '}to send · {' '}
            <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-mono">Shift+Enter</kbd>
            {' '}for new line
          </p>
        )}
        {attachmentDisabledReason && (
          <p id={attachReasonId} className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
            {attachmentDisabledReason}
          </p>
        )}
      </div>
    );
  }
);
ChatInput.displayName = 'ChatInput';
